/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BreadcrumbLevel, BreadcrumbType, ErrorTracker, IBreadcrumb } from "@adapptio/lib-components";
import { isFunction, isString } from "@adapptio/lib-shared";

export interface ILogBreadcrumbParams {
	type?: BreadcrumbType;
	level?: BreadcrumbLevel;
	category?: string;
	message?: string | ((...args: unknown[]) => string);
	data?: Record<string, unknown> | ((...args: unknown[]) => Record<string, unknown>);
}

export function Breadcrumb(params: ILogBreadcrumbParams): MethodDecorator;
export function Breadcrumb(
	category: ILogBreadcrumbParams["category"],
	message?: ILogBreadcrumbParams["message"],
	data?: ILogBreadcrumbParams["data"]
): MethodDecorator;

export function Breadcrumb(
	params: ILogBreadcrumbParams["category"] | ILogBreadcrumbParams = {},
	message?: ILogBreadcrumbParams["message"],
	data?: ILogBreadcrumbParams["data"]
): MethodDecorator {
	return function (target, propertyKey, descriptor) {
		const original = descriptor.value;

		const paramsObj: ILogBreadcrumbParams = isString(params)
			? {
					category: params,
					message: message || ((firstArg) => String(firstArg)),
					data
			  }
			: params;

		// @ts-ignore
		descriptor.value = function (...args) {
			const breadcrumb: IBreadcrumb = {
				type: paramsObj.type || "debug",
				level: paramsObj.level || "info",
				category: paramsObj.category || "log",
				message: isFunction(paramsObj.message)
					? paramsObj.message(...args)
					: paramsObj.message || `Method "${String(propertyKey)}" executed`,
				timestamp: new Date().getTime(),
				data: isFunction(paramsObj.data) ? paramsObj.data(...args) : paramsObj.data || { args }
			};

			const errorTracker = ErrorTracker.getInstance();

			errorTracker.addBreadcrumb(breadcrumb);

			// @ts-ignore
			return original.call(this, ...args);
		};
	};
}
